/* eslint-disable jsx-a11y/img-redundant-alt */
import React, { useEffect, useState } from "react";
import {
  Modal,
  message,
  Select,
} from "antd";

import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  Typography,
  FormLabel,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import MicIcon from "@mui/icons-material/Mic";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import axios from "../../axiosInstance";

const { Option } = Select;
const { confirm } = Modal;

const SpeechRecognition =
  window.SpeechRecognition || window.webkitSpeechRecognition;

const recognition = SpeechRecognition
  ? new SpeechRecognition()
  : null;

const EditQuestionModal = ({
  editQuestionModal,
  setEditQuestionModal,
  refreshData,
  examId,
  selectedQuestion,
  setSelectedQuestion,
}) => {
  const questionId = selectedQuestion?._id;

  const [questionText, setQuestionText] = useState("");
  const [questionImage, setQuestionImage] = useState("");

  const [options, setOptions] = useState([
    { text: "", image: "" },
    { text: "", image: "" },
  ]);

  const [correctOptions, setCorrectOptions] = useState([]);

  const [marks, setMarks] = useState(1);

  // const [language, setLanguage] = useState("hi-IN");

    const [language, setLanguage] = useState("en-IN");
  

  const [loading, setLoading] = useState(false);

  // =========================
  // GET QUESTION DETAILS
  // =========================

  const getQuestionById = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `/api/v1/question/${questionId}`
      );

      console.log("Question Response:", response?.data);

      const data = response?.data?.data;

      if (!data) {
        message.error("Question data not found");
        return;
      }

      setQuestionText(data?.questionText || "");
      setQuestionImage(data?.questionImage || "");

      setOptions(
        data?.options?.length > 0
          ? data.options
          : [
              { text: "", image: "" },
              { text: "", image: "" },
            ]
      );

      // Correct option indexes
      const correctIndexes =
        data?.correctOptions?.map(
          (item) => item.optionIndex
        ) || [];

      setCorrectOptions(correctIndexes);

      setMarks(data?.marks || 1);
    } catch (error) {
      console.log(error);

      message.error(
        error?.response?.data?.message ||
          "Failed to fetch question"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (questionId && editQuestionModal) {
      getQuestionById();
    }
  }, [questionId, editQuestionModal]);

  // =========================
  // OPTION CHANGE
  // =========================

  const handleOptionChange = (value, index) => {
    const updatedOptions = [...options];

    updatedOptions[index].text = value;

    setOptions(updatedOptions);
  };

  // =========================
  // ADD OPTION
  // =========================

  const handleAddOption = () => {
    setOptions([
      ...options,
      {
        text: "",
        image: "",
      },
    ]);
  };

  // =========================
  // DELETE OPTION
  // =========================

  const handleDeleteOption = (index) => {
    confirm({
      title: "Delete Option",
      content: "Are you sure you want to delete this option?",
      okText: "Yes",
      cancelText: "No",

      onOk() {
        const updatedOptions = options.filter(
          (_, i) => i !== index
        );

        setOptions(updatedOptions);

        // remove deleted option from correct answer
        const updatedCorrectOptions = correctOptions
          .filter((item) => item !== index)
          .map((item) =>
            item > index ? item - 1 : item
          );

        setCorrectOptions(updatedCorrectOptions);
      },
    });
  };

  // =========================
  // CORRECT OPTION
  // =========================

  const handleCorrectOption = (checked, index) => {
    if (checked) {
      setCorrectOptions((prev) => [...prev, index]);
    } else {
      setCorrectOptions((prev) =>
        prev.filter((item) => item !== index)
      );
    }
  };

  // =========================
  // IMAGE TO BASE64
  // =========================

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = () => resolve(reader.result);

      reader.onerror = (error) => reject(error);
    });
  };

  // =========================
  // QUESTION IMAGE
  // =========================

  const handleQuestionImage = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const base64 = await convertToBase64(file);

    setQuestionImage(base64);
  };

  // =========================
  // OPTION IMAGE
  // =========================

  const handleOptionImage = async (e, index) => {
    const file = e.target.files[0];

    if (!file) return;

    const base64 = await convertToBase64(file);

    const updatedOptions = [...options];

    updatedOptions[index].image = base64;

    setOptions(updatedOptions);
  };

  // =========================
  // SPEECH TO TEXT
  // =========================

  const handleSpeech = (type, index = null) => {
    if (!recognition) {
      message.error(
        "Speech recognition not supported in browser"
      );
      return;
    }

    recognition.lang = language;

    recognition.start();

    recognition.onresult = (event) => {
      const transcript =
        event.results[0][0].transcript;

      if (type === "question") {
        setQuestionText(
          (prev) => prev + " " + transcript
        );
      }

      if (type === "option") {
        const updatedOptions = [...options];

        updatedOptions[index].text =
          updatedOptions[index].text +
          " " +
          transcript;

        setOptions(updatedOptions);
      }
    };

    recognition.onerror = (event) => {
      message.error(event.error);
    };
  };

  // =========================
  // UPDATE QUESTION
  // =========================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!questionText) {
      message.error("Question is required");
      return;
    }

    if (options.length < 2) {
      message.error("Minimum 2 options required");
      return;
    }

    if (correctOptions.length === 0) {
      message.error("Please select correct option");
      return;
    }

    const payload = {
      questionText,
      questionImage,
      options,
      correctOptions: correctOptions.map(
        (index) => ({
          optionIndex: index,
          text: options[index]?.text,
        })
      ),
      marks,
      exam: examId,
      questionType:
        correctOptions.length > 1
          ? "multiple"
          : "single",
    };

    console.log("Update Payload:", payload);

    confirm({
      title: "Update Question",
      content:
        "Are you sure you want to update this question?",

      okText: "Yes",
      cancelText: "No",

      async onOk() {
        try {
          setLoading(true);

          const response = await axios.put(
            `/api/v1/question/${questionId}`,
            payload
          );

          console.log(response?.data);

          if (response?.data?.success) {
            message.success(
              response?.data?.message ||
                "Question updated successfully"
            );

            refreshData();

            setEditQuestionModal(false);

            setSelectedQuestion(null);
          } else {
            message.error(
              response?.data?.message
            );
          }
        } catch (error) {
          console.log(error);

          message.error(
            error?.response?.data?.message ||
              "Failed to update question"
          );
        } finally {
          setLoading(false);
        }
      },
    });
  };

  return (
    <Modal
      title="Edit Question"
      open={editQuestionModal}
      footer={false}
      width={1400}
      onCancel={() => {
        setEditQuestionModal(false);
        setSelectedQuestion(null);
      }}
    >
      <Container
        component="form"
        onSubmit={handleSubmit}
      >
        {/* LANGUAGE */}

        <Box sx={{ mb: 2 }}>
          <Select
            value={language}
            onChange={(value) =>
              setLanguage(value)
            }
            style={{ width: 220 }}
          >
            <Option value="en-IN">
              English
            </Option>

            <Option value="hi-IN">
              Hindi
            </Option>
          </Select>

           
        </Box>

         <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <FormLabel>Marks:</FormLabel>
            <input
              type="number"
              min="1"
              value={marks}
              onChange={(e) => setMarks(parseInt(e.target.value))}
              style={{ width: "80px", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
            />
          </Box>

        {/* QUESTION */}

        <Typography
          variant="h6"
          sx={{ mb: 1 }}
        >
          Question
        </Typography>

        <ReactQuill
          theme="snow"
          value={questionText}
          onChange={(value) =>
            setQuestionText(value)
          }
          placeholder="Enter question"
        />

        {/* QUESTION ACTIONS */}

        <Box
          sx={{
            mt: 2,
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<MicIcon />}
            onClick={() =>
              handleSpeech("question")
            }
          >
            Speak
          </Button>

          <input
            type="file"
            accept="image/*"
            id="question-image"
            hidden
            onChange={handleQuestionImage}
          />

          <label htmlFor="question-image">
            <Button
              variant="outlined"
              component="span"
            >
              Upload Question Image
            </Button>
          </label>

          {questionImage && (
            <img
              src={questionImage}
              alt="question"
              style={{
                width: 80,
                height: 80,
                objectFit: "cover",
                borderRadius: 5,
              }}
            />
          )}
        </Box>

        {/* OPTIONS */}

        <Grid
          container
          spacing={3}
          sx={{ mt: 2 }}
        >
          {options?.map((option, index) => (
            <Grid
              item
              xs={12}
              md={6}
              key={index}
            >
              <Box
                sx={{
                  border: "1px solid #ddd",
                  p: 2,
                  borderRadius: 2,
                }}
              >
                <Typography
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Option{" "}
                  {String.fromCharCode(
                    65 + index
                  )}
                </Typography>

                <ReactQuill
                  theme="snow"
                  value={option?.text || ""}
                  onChange={(value) =>
                    handleOptionChange(
                      value,
                      index
                    )
                  }
                  placeholder={`Option ${
                    index + 1
                  }`}
                />

                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={correctOptions.includes(
                          index
                        )}
                        onChange={(e) =>
                          handleCorrectOption(
                            e.target.checked,
                            index
                          )
                        }
                      />
                    }
                    label="Correct"
                  />

                  <Button
                    variant="outlined"
                    startIcon={<MicIcon />}
                    onClick={() =>
                      handleSpeech(
                        "option",
                        index
                      )
                    }
                  >
                    Speak
                  </Button>

                  <input
                    type="file"
                    accept="image/*"
                    id={`option-image-${index}`}
                    hidden
                    onChange={(e) =>
                      handleOptionImage(
                        e,
                        index
                      )
                    }
                  />

                  <label
                    htmlFor={`option-image-${index}`}
                  >
                    <Button
                      variant="outlined"
                      component="span"
                    >
                      Upload Image
                    </Button>
                  </label>

                  {option?.image && (
                    <img
                      src={option?.image}
                      alt="option"
                      style={{
                        width: 60,
                        height: 60,
                        objectFit: "cover",
                        borderRadius: 5,
                      }}
                    />
                  )}

                  {options.length > 2 && (
                    <IconButton
                      color="error"
                      onClick={() =>
                        handleDeleteOption(
                          index
                        )
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* ADD OPTION */}

        <Button
          variant="outlined"
          color="success"
          startIcon={<AddIcon />}
          sx={{ mt: 3 }}
          onClick={handleAddOption}
        >
          Add More Option
        </Button>

        {/* SUBMIT */}

        <Box sx={{ mt: 4 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading
              ? "Updating..."
              : "Update Question"}
          </Button>
        </Box>
      </Container>
    </Modal>
  );
};

export default EditQuestionModal;